import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TimelineButtons } from '../components/TimelineButtons';
import { toast } from 'sonner';
import { format, parseISO, subDays, startOfDay } from 'date-fns';
import { 
    CalendarIcon, Plus, X, ChevronLeft, ChevronRight, 
    Trash2, Save, Utensils, AlertTriangle, CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

export default function FoodTracker() {
    const { token } = useAuth();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [entries, setEntries] = useState([]);
    const [aipFoods, setAipFoods] = useState({ compliant: [], avoid: [] });
    const [loading, setLoading] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [showAddMeal, setShowAddMeal] = useState(false);
    const [stats, setStats] = useState(null);
    
    // New meal state
    const [mealType, setMealType] = useState('breakfast');
    const [foods, setFoods] = useState([]);
    const [newFood, setNewFood] = useState('');
    const [reactionSeverity, setReactionSeverity] = useState(null);
    const [mealNotes, setMealNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchAipFoods = async () => {
            try {
                const res = await fetch(`${API_URL}/api/food/aip-foods`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setAipFoods(data);
                }
            } catch (error) {
                console.error('Failed to fetch AIP foods:', error);
            }
        };
        fetchAipFoods();
    }, [token]);

    const fetchEntriesForDate = useCallback(async (date) => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            console.log('[FoodTracker] Fetching entries for date:', dateStr);
            const res = await fetch(`${API_URL}/api/food/date/${dateStr}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                console.log('[FoodTracker] Entries received:', data.length, 'meals');
                console.log('[FoodTracker] Foods logged:', data.map(e => ({ meal: e.meal_type, foods: e.foods?.map(f => f.name) })));
                setEntries(data || []);
            } else {
                console.error('[FoodTracker] API error:', res.status);
            }
        } catch (error) {
            console.error('[FoodTracker] Failed to fetch entries:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchStats = useCallback(async () => {
        try {
            console.log('[FoodTracker] Fetching food stats...');
            const res = await fetch(`${API_URL}/api/food/stats/summary?days=30`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log('[FoodTracker] Stats received:', data);
                console.log('[FoodTracker] Data feeding into correlations - Total entries:', data.total_entries, 'Compliant %:', data.compliant_percentage);
                if (data.reaction_foods?.length > 0) {
                    console.log('[FoodTracker] Reaction foods (for correlation analysis):', data.reaction_foods);
                }
                setStats(data);
            } else {
                console.error('[FoodTracker] Stats API error:', res.status);
            }
        } catch (error) {
            console.error('[FoodTracker] Failed to fetch stats:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchEntriesForDate(selectedDate);
        fetchStats();
    }, [selectedDate, fetchEntriesForDate, fetchStats]);

    const addFood = (name, isCompliant = null) => {
        if (!name.trim()) return;
        if (foods.find(f => f.name.toLowerCase() === name.toLowerCase())) {
            toast.error('Food already added');
            return;
        }
        
        const compliant = isCompliant !== null ? isCompliant : 
            aipFoods.compliant.some(f => f.toLowerCase() === name.toLowerCase());
        
        setFoods([...foods, { name: name.trim(), aip_compliant: compliant, reaction: false, notes: '' }]);
        setNewFood('');
    };

    const updateFood = (index, field, value) => {
        const updated = [...foods];
        updated[index] = { ...updated[index], [field]: value };
        setFoods(updated);
    };

    const removeFood = (index) => {
        setFoods(foods.filter((_, i) => i !== index));
    };

    const saveMeal = async () => {
        if (foods.length === 0) {
            toast.error('Please add at least one food');
            return;
        }

        setSaving(true);
        try {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const res = await fetch(`${API_URL}/api/food`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    date: dateStr,
                    meal_type: mealType,
                    foods,
                    reaction_severity: reactionSeverity,
                    notes: mealNotes || null
                })
            });

            if (res.ok) {
                toast.success('Meal saved successfully');
                setShowAddMeal(false);
                setFoods([]);
                setReactionSeverity(null);
                setMealNotes('');
                fetchEntriesForDate(selectedDate);
                fetchStats();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save meal');
        } finally {
            setSaving(false);
        }
    };

    const deleteMeal = async (entryId) => {
        if (!window.confirm('Delete this meal entry?')) return;

        try {
            const res = await fetch(`${API_URL}/api/food/${entryId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Meal deleted');
                fetchEntriesForDate(selectedDate);
                fetchStats();
            }
        } catch (error) {
            toast.error('Failed to delete meal');
        }
    };

    const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
    const goToNextDay = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (selectedDate < startOfDay(tomorrow)) {
            setSelectedDate(prev => {
                const next = new Date(prev);
                next.setDate(next.getDate() + 1);
                return next;
            });
        }
    };

    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Food Tracker</h1>
                    <p className="text-muted-foreground mt-1">Track your meals and identify food reactions</p>
                </div>
                <TimelineButtons />
            </div>

            {stats && stats.total_entries > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-emerald-200">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500 rounded-lg">
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">AIP Compliant</p>
                                    <p className="text-xl font-bold">{stats.compliant_percentage || 0}%</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-500 rounded-lg">
                                    <Utensils className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Meals Logged</p>
                                    <p className="text-xl font-bold">{stats.total_entries}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {stats.reaction_foods?.length > 0 && (
                        <Card className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/20">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-red-500 rounded-lg">
                                        <AlertTriangle className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Top Reaction</p>
                                        <p className="text-sm font-bold truncate">{stats.reaction_foods[0]?.name}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <Button variant="ghost" size="icon" onClick={goToPreviousDay} data-testid="prev-day-btn">
                                    <ChevronLeft className="w-5 h-5" />
                                </Button>
                                
                                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="min-w-[200px]" data-testid="date-picker-btn">
                                            <CalendarIcon className="w-4 h-4 mr-2" />
                                            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="center">
                                        <Calendar
                                            mode="single"
                                            selected={selectedDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedDate(date);
                                                    setCalendarOpen(false);
                                                }
                                            }}
                                            disabled={(date) => date > new Date()}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Button variant="ghost" size="icon" onClick={goToNextDay} disabled={isToday} data-testid="next-day-btn">
                                    <ChevronRight className="w-5 h-5" />
                                </Button>
                            </div>
                            {isToday && <p className="text-center text-sm text-muted-foreground mt-2">Today</p>}
                        </CardContent>
                    </Card>

                    {/* Today's Meals */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>Meals</CardTitle>
                                <CardDescription>Food logged for this day</CardDescription>
                            </div>
                            <Button onClick={() => setShowAddMeal(true)} data-testid="add-meal-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Meal
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : entries.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Utensils className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No meals logged for this day</p>
                                    <Button variant="link" onClick={() => setShowAddMeal(true)}>Add your first meal</Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {entries.map((entry) => (
                                        <div key={entry.id} className="p-4 rounded-xl border bg-muted/30">
                                            <div className="flex items-center justify-between mb-3">
                                                <Badge variant="outline" className="capitalize">{entry.meal_type}</Badge>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteMeal(entry.id)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {entry.foods.map((food, idx) => (
                                                    <Badge 
                                                        key={idx} 
                                                        variant={food.aip_compliant ? "default" : "destructive"}
                                                        className={food.reaction ? "ring-2 ring-red-500" : ""}
                                                    >
                                                        {food.name}
                                                        {food.reaction && " ⚠️"}
                                                    </Badge>
                                                ))}
                                            </div>
                                            {entry.reaction_severity && (
                                                <p className="text-xs text-red-500 mt-2">Reaction severity: {entry.reaction_severity}/10</p>
                                            )}
                                            {entry.notes && <p className="text-xs text-muted-foreground mt-2">{entry.notes}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Add Meal Form */}
                    {showAddMeal && (
                        <Card className="border-2 border-indigo-200 dark:border-indigo-800">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Add Meal</CardTitle>
                                    <Button variant="ghost" size="icon" onClick={() => setShowAddMeal(false)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium mb-2 block">Meal Type</label>
                                    <Select value={mealType} onValueChange={setMealType}>
                                        <SelectTrigger data-testid="meal-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MEAL_TYPES.map(type => (
                                                <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Quick Add AIP Compliant Foods</label>
                                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                                        {aipFoods.compliant.slice(0, 15).map((food) => (
                                            <Badge
                                                key={food}
                                                variant={foods.find(f => f.name === food) ? "default" : "outline"}
                                                className="cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900"
                                                onClick={() => addFood(food, true)}
                                            >
                                                {food}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Add Food</label>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Enter food name..."
                                            value={newFood}
                                            onChange={(e) => setNewFood(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addFood(newFood)}
                                            data-testid="food-input"
                                        />
                                        <Button onClick={() => addFood(newFood)} disabled={!newFood.trim()} data-testid="add-food-btn">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                {foods.length > 0 && (
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Foods ({foods.length})</label>
                                        <div className="space-y-2">
                                            {foods.map((food, index) => (
                                                <div key={index} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                                                    <span className="flex-1">{food.name}</span>
                                                    <Badge variant={food.aip_compliant ? "default" : "destructive"} className="text-xs">
                                                        {food.aip_compliant ? "AIP OK" : "Avoid"}
                                                    </Badge>
                                                    <Button
                                                        variant={food.reaction ? "destructive" : "outline"}
                                                        size="sm"
                                                        onClick={() => updateFood(index, 'reaction', !food.reaction)}
                                                    >
                                                        {food.reaction ? "Reaction!" : "No Reaction"}
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeFood(index)}>
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {foods.some(f => f.reaction) && (
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Reaction Severity</label>
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="range"
                                                min="1"
                                                max="10"
                                                value={reactionSeverity || 5}
                                                onChange={(e) => setReactionSeverity(parseInt(e.target.value))}
                                                className="flex-1"
                                            />
                                            <Badge variant="destructive">{reactionSeverity || 5}/10</Badge>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Notes</label>
                                    <Textarea
                                        placeholder="How did you feel after this meal?"
                                        value={mealNotes}
                                        onChange={(e) => setMealNotes(e.target.value)}
                                        rows={2}
                                    />
                                </div>

                                <Button onClick={saveMeal} disabled={saving || foods.length === 0} className="w-full" data-testid="save-meal-btn">
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? 'Saving...' : 'Save Meal'}
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </div>

                <div className="space-y-6">
                    <Card className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                Foods to Avoid (AIP)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-1">
                                {aipFoods.avoid.map((food) => (
                                    <Badge key={food} variant="outline" className="text-xs border-red-300 text-red-700 dark:text-red-300">
                                        {food}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {stats?.reaction_foods?.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Your Reaction Foods</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {stats.reaction_foods.map((f, idx) => (
                                        <div key={idx} className="flex items-center justify-between">
                                            <span className="text-sm">{f.name}</span>
                                            <Badge variant="destructive">{f.count}x</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">🥗 AIP Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Focus on whole, unprocessed foods</p>
                            <p>• Reintroduce foods one at a time</p>
                            <p>• Wait 3-7 days between reintroductions</p>
                            <p>• Track reactions carefully</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
